import { useMemo } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { useSubscription } from "@/contexts/billing/subscription";
import { useGenieLimits } from "@/hooks/agent/useAgents";
import { useRole } from "@/hooks/auth/useRole";
import { Roles } from "@/types/auth/auth";

export interface GenieAccessScope {
  canCreateGenie: boolean;
  currentGenieCount: number;
  maxGenies: number;
  isAtLimit: boolean;
  requiresUpgrade: boolean;
}

export interface GenieAccessInfo extends GenieAccessScope {
  isLoading: boolean;
  personal: GenieAccessScope;
  team: GenieAccessScope | null;
}

function toScope(scope: { currentCount: number; maxGenies: number; isAtLimit: boolean }, isSubscribed: boolean): GenieAccessScope {
  return {
    canCreateGenie: !scope.isAtLimit,
    currentGenieCount: scope.currentCount,
    maxGenies: scope.maxGenies,
    isAtLimit: scope.isAtLimit,
    requiresUpgrade: scope.isAtLimit && !isSubscribed,
  };
}

const EMPTY_SCOPE: GenieAccessScope = {
  canCreateGenie: false,
  currentGenieCount: 0,
  maxGenies: 1,
  isAtLimit: false,
  requiresUpgrade: false,
};

export const useAccessLimits = () => {
  const { user } = useAuth();
  const { isSubscribed } = useSubscription();
  const { data: limits, isLoading: isLimitsLoading } = useGenieLimits();
  const { hasRole } = useRole();

  const genieAccess = useMemo<GenieAccessInfo>(() => {
    const isAdmin = hasRole(Roles.Admin);

    if (!user || isLimitsLoading || !limits) {
      return {
        ...EMPTY_SCOPE,
        isLoading: isLimitsLoading,
        personal: EMPTY_SCOPE,
        team: null,
      };
    }

    const personal = isAdmin
      ? { ...toScope({ currentCount: limits.personal.currentCount, maxGenies: Infinity, isAtLimit: false }, isSubscribed) }
      : toScope(limits.personal, isSubscribed);
    const team = limits.team
      ? isAdmin
        ? toScope({ currentCount: limits.team.currentCount, maxGenies: Infinity, isAtLimit: false }, isSubscribed)
        : toScope(limits.team, isSubscribed)
      : null;

    // Backward-compatible flat shape: creation is gated on whether the user has capacity
    // in *any* scope (new genies default to team when one exists, but personal stays an
    // opt-out). Reported counts/limits default to the team scope when present, since that's
    // what most consumers care to display, but canCreateGenie/requiresUpgrade consider both.
    const canCreateGenie = personal.canCreateGenie || (team?.canCreateGenie ?? false);
    const requiresUpgrade = !canCreateGenie && (personal.requiresUpgrade || (team?.requiresUpgrade ?? false));
    const displayScope = team ?? personal;

    return {
      ...displayScope,
      canCreateGenie,
      requiresUpgrade,
      isLoading: false,
      personal,
      team,
    };
  }, [user, limits, isLimitsLoading, isSubscribed, hasRole]);

  return {
    genie: genieAccess,
  };
};

export const useGenieAccess = () => {
  const { genie } = useAccessLimits();
  return genie;
};
